import { forwardRef, useState } from "react";
import PDW_PlaceTabHomeSection from "./pdw-place-tab-home-section";
import PDW_PlaceTabTidingsSection from "./pdw-place-tab-tiding-section";
import PDW_PlaceTabMenuSection from "./pdw-place-tab-menu-section";
import PDW_PlaceTabReviewsSection from "./pdw-place-tab-review-section";
import PDW_PlaceTabPicturesSection from "./pdw-place-tab-pictures-section";
import PDW_PlaceTabInformationSection from "./pdw-place-tab-information-section";
import { useTranslations } from "@/lib/static-intl";
import PlacesImagesSlider from "../../places-images-slider";
import PlaceReviewCreateModal from "../../place-review-create-modal";
import { isApiPlace } from "@/utils/placeUtils";

// Define tabs based on place type - hide Menu tab for API places
const getTabs = (place) => {
  // console.log("[DEBUG] getTabs (places-comps) - checking place for API detection, place:", place);
  // console.log("[DEBUG] getTabs (places-comps) - place.source:", place?.source);
  // console.log("[DEBUG] getTabs (places-comps) - place.id:", place?.id);
  // console.log("[DEBUG] getTabs (places-comps) - place._id:", place?._id);
  // console.log("[DEBUG] getTabs (places-comps) - place.placeUniqueNumber:", place?.placeUniqueNumber);
  
  const baseTabs = ["Home", "Reviews", "Photos", "Information"];
  
  // Only include Menu tab for DB places (not API places)
  const apiPlaceResult = isApiPlace(place);
  // console.log("[DEBUG] getTabs (places-comps) - isApiPlace result:", apiPlaceResult);
  
  // Only show Menu tab for DB places (not API places)
  // API places are external data without user-generated menus
  if (!apiPlaceResult) {
    baseTabs.splice(1, 0, "Menu"); // Insert Menu tab at position 1
    // console.log("[DEBUG] getTabs (places-comps) - Added Menu tab for DB place");
  } else {
    // console.log("[DEBUG] getTabs (places-comps) - Skipped Menu tab for API place");
  }
  
  // console.log("[DEBUG] getTabs (places-comps) - Final tabs:", baseTabs);
  return baseTabs;
};

const PDW_PlaceTabsSection = forwardRef(
  (
    {
      place,
      className,
      currentTab,
      setCurrentTab,
      scrollToThirdChild,
      isCssForDialog,
      userFollowing = [],
      placeMenus,
      placeMedia,
      placePosts,
      placeTextReviews = [],
      placesMenuLoader,
      setUserFollowing,
      openFullScreenModal,
      handleContentModalOpen = () => {},
      resetModal,
      setPlacePosts,
      setPlaceTextReviews,
      fetchPlacePosts,
      fetchPlaceTextReview,
    },
    ref
  ) => {
    const t = useTranslations("places.place_details_window");

    const [isReviewCreateModalOpen, setIsReviewCreateModalOpen] =
      useState(false);

    const [currentSliderIndex, setCurrentSliderIndex] = useState(0);
    const [filePathsInSlider, setFilePathsInSlider] = useState(null);

    const openReviewCreateModal = () => {
      setIsReviewCreateModalOpen(true);
    };

    const closeReviewCreateModal = () => {
      setIsReviewCreateModalOpen(false);
    };

    const openPlaceMediaSlider = (filePaths, ind) => {
      if (filePaths && Array.isArray(filePaths))
        setFilePathsInSlider(filePaths);
      if (!isNaN(Number(ind))) setCurrentSliderIndex(ind);
    };

    const renderCurrentTabSection = (tab, place) => {
      switch (tab) {
        case "Home":
          return (
            <PDW_PlaceTabHomeSection
              place={place}
              setCurrentTab={(tab) => {
                scrollToThirdChild();
                setCurrentTab(tab);
              }}
              placeMenus={placeMenus}
              placeMedia={placeMedia}
              placeTextReviews={placeTextReviews}
              placesMenuLoader={placesMenuLoader}
              openPlaceMediaSlider={openPlaceMediaSlider}
              isCssForDialog={isCssForDialog}
              openFullScreenModal={openFullScreenModal}
              handleContentModalOpen={handleContentModalOpen}
            />
          );
        case "Tidings":
          return (
            <PDW_PlaceTabTidingsSection
              place={place}
              isCssForDialog={isCssForDialog}
            />
          );
        case "Menu":
          return (
            <PDW_PlaceTabMenuSection
              place={place}
              placeMenus={placeMenus}
              isCssForDialog={isCssForDialog}
              openPlaceMediaSlider={openPlaceMediaSlider}
            />
          );
        case "Reviews":
          return (
            <PDW_PlaceTabReviewsSection
              place={place}
              placePosts={placePosts}
              setPlacePosts={setPlacePosts}
              setPlaceTextReviews={setPlaceTextReviews}
              placeTextReviews={placeTextReviews}
              userFollowing={userFollowing}
              setUserFollowing={setUserFollowing}
              isCssForDialog={isCssForDialog}
              openFullScreenModal={openFullScreenModal}
              handleContentModalOpen={handleContentModalOpen}
              openReviewCreateModal={openReviewCreateModal}
              openPlaceMediaSlider={openPlaceMediaSlider}
              resetModal={resetModal}
              fetchPlacePosts={fetchPlacePosts}
              fetchPlaceTextReview={fetchPlaceTextReview}
            />
          );
        case "Photos":
          return (
            <PDW_PlaceTabPicturesSection
              place={place}
              placeMedia={placeMedia}
              isCssForDialog={isCssForDialog}
              openPlaceMediaSlider={openPlaceMediaSlider}
            />
          );
        case "Information":
          return (
            <PDW_PlaceTabInformationSection
              place={place}
              isCssForDialog={isCssForDialog}
            />
          );
        default:
          return null;
      }
    };

    return (
      <>
        <div ref={ref} className={`w-full flex flex-col gap-2 ${className}`}>
          <div
            className={`w-full sticky -top-1 left-0 grid grid-cols-5 items-center ${
              isCssForDialog
                ? "px-1 gap-0 !-top-1 sm:!top-[48px] border-t -mt-2"
                : "px-4 gap-2"
            } cursor-pointer border-b dark:border-white/30 bg-white dark:bg-dark_bg_grey z-[34]`}
          >
            {getTabs(place).map((tab, i) => (
              <button
                key={i}
                type="button"
                className={`w-full ${
                  isCssForDialog ? "text-xs py-3" : "text-base py-4"
                } border-b-2 capitalize transition ease-in-out ${
                  currentTab === tab
                    ? "border-black dark:border-white text-black dark:text-white font-medium"
                    : "border-transparent text-black/30 dark:text-white/30"
                }`}
                onClick={() => {
                  scrollToThirdChild();
                  setCurrentTab(tab);
                }}
              >
                {t(tab)}
              </button>
            ))}
          </div>
          <div className="w-full">
            {renderCurrentTabSection(currentTab, place)}
          </div>
        </div>
        <PlacesImagesSlider
          isOpen={!!filePathsInSlider}
          filePaths={filePathsInSlider}
          currentSliderIndex={currentSliderIndex}
          placeMedia={placeMedia}
          onClose={() => {
            setFilePathsInSlider(null);
            setCurrentSliderIndex(0);
          }}
        />
        <PlaceReviewCreateModal
          placeId={place?._id || place?.id || place?.contentid || place?.contentId}
          isOpen={isReviewCreateModalOpen}
          onClose={closeReviewCreateModal}
          resetModal={resetModal}
        />
      </>
    );
  }
);

export default PDW_PlaceTabsSection;
